import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Grid } from '@mui/material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { Alert } from 'components/atoms/Alert';
import Button from 'components/atoms/Button';
import { Loader } from 'components/atoms/Loader';
import { CardList } from 'components/molecules/CardList';
import { Pagination } from 'components/molecules/Pagination';
import { Table } from 'components/molecules/Table';

import { useTranslations } from 'hooks/useTranslations';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { useAlert } from 'models/alertContext';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { ERouteLinks } from 'models/route';
import { EPerPages, ISortOptions } from 'models/table';
import { ActionTypes } from 'state/actions/alert';
import { EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';

import { ISearchParams, objectToQueryString } from 'helpers/searchQuery';

import { useConfigurationLicence } from './hooks';
import { LicenceSearchForm } from './LicenceSearchForm';
import LicenceConfiguratorTableCard from './licenceConfiguratorTableCard';
import {
  initialSearchFormValues,
  ILicenceConfigurationSearchFormValues,
  licenceConfiguratorTableColumns,
  mapLicenceConfiguratorDataSource,
} from './helper';
import { StyledPageTitle } from 'theme/styles';
import { formatDateTimeToIso } from 'utils/date';
import { replaceUrlIfOnPage } from 'utils/history';
import { EDictionaryTypes } from 'models/dictionary';

const QUERY_KEYS = {
  currentPage: 'currentPage',
  perPage: 'perPage',
} as const;

export const LicenceConfigurationPage = () => {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isDesktop } = useThemeBreakpoint();

  const { shortDateFormat, getProperties } = useGlobalProperty();
  const { clearAlert, nextPageAlert, samePageAlert } = useAlert();

  const {
    licences,
    getLicencesData,
    paginator,
    weaponLicenceTemplateStatus,
    loadDictionaries,
  } = useConfigurationLicence();

  const [searchParams] = useSearchParams();

  const perPageFromQuery = Number.parseInt(
    searchParams.get(QUERY_KEYS.perPage) || String(EPerPages.perPage25),
    10,
  );
  const currentPageFromQuery = Number.parseInt(
    searchParams.get(QUERY_KEYS.currentPage) || '1',
    10,
  );

  const [currentPage, setCurrentPage] = useState<number>(currentPageFromQuery);
  const [perPage, setPerPage] = useState<EPerPages>(perPageFromQuery as EPerPages);

  const [sort, setSort] = useState<ISortOptions | null>(null);

  const [licenceQuery, setLicenceQuery] = useState<
    Partial<ILicenceConfigurationSearchFormValues>
  >({});

  const [initialValues, setInitialValues] =
    useState<ILicenceConfigurationSearchFormValues | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isPaginationLoading, setIsPaginationLoading] = useState<boolean>(false);

  const scrollPosition = useRef(0);
  const previousSortRef = useRef<ISortOptions | null>(null);

  useEffect(() => {
    loadDictionaries([EDictionaryTypes.weaponLicenceTemplateStatus]);
  }, []);

  useEffect(() => {
    getProperties();
  }, []);

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_NEXT_PAGE_ALERT);
      clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
    },
    [],
  );

  useEffect(() => {
    const query = new URLSearchParams(searchParams);

    const queryValues: ISearchParams = {};
    query.forEach((value, field) => {
      if (field in initialSearchFormValues) {
        queryValues[field] = value;
      }
    });

    const initialFormValues = { ...initialSearchFormValues, ...queryValues };
    setInitialValues(initialFormValues as ILicenceConfigurationSearchFormValues);

    setLicenceQuery(queryValues as Partial<ILicenceConfigurationSearchFormValues>);

    setCurrentPage(currentPageFromQuery);
    setPerPage(perPageFromQuery as EPerPages);
  }, [searchParams]);

  useEffect(() => {
    (async () => {
      const isSortChanged = sort !== previousSortRef.current;

      if (isSortChanged) {
        scrollPosition.current = window.scrollY;
        setIsLoading(true);
        setIsPaginationLoading(false);
      } else if (licences?.length) {
        setIsPaginationLoading(true);
      } else {
        setIsLoading(true);
      }

      await getLicencesData(currentPage, perPage, licenceQuery, sort);

      setIsLoading(false);
      setIsPaginationLoading(false);

      if (isSortChanged) {
        window.scrollTo({ top: scrollPosition.current, behavior: 'auto' });
        scrollPosition.current = 0;
      }

      previousSortRef.current = sort;
    })();
  }, [currentPage, perPage, licenceQuery, sort]);

  const getNewQueryPath = (
    values: Partial<ILicenceConfigurationSearchFormValues>,
    nextPage = currentPage,
    nextPerPage = perPage,
  ) => {
    const valuesWithoutPreset = { ...values, preset: undefined };

    const query = objectToQueryString({
      ...valuesWithoutPreset,
      [QUERY_KEYS.currentPage]: nextPage,
      [QUERY_KEYS.perPage]: nextPerPage,
    });

    return query.length ? `${pathname}?${query}` : pathname;
  };

  const handleSetUserQuery = (values: Partial<ILicenceConfigurationSearchFormValues>) => {
    const cleaned = {
      ...values,
      createDateTo: values.createDateTo
        ? formatDateTimeToIso(values.createDateTo, true).toString()
        : values.createDateTo,
      createDateFrom: values.createDateFrom
        ? formatDateTimeToIso(values.createDateFrom).toString()
        : values.createDateFrom,
      preset: undefined,
    };
    const nextPage = 1;

    replaceUrlIfOnPage(getNewQueryPath(cleaned, nextPage, perPage));
    setCurrentPage(nextPage);
    setLicenceQuery(cleaned);
  };

  const handleChangePage = (page: number) => {
    replaceUrlIfOnPage(getNewQueryPath(licenceQuery, page, perPage));
    setCurrentPage(page);
  };

  const handleChangePerPage = (value: EPerPages) => {
    const nextPage = 1;
    replaceUrlIfOnPage(getNewQueryPath(licenceQuery, nextPage, value));
    setPerPage(value);
    setCurrentPage(nextPage);
  };

  const handleView = (id: string) => {
    navigate(ERouteLinks.LicenceTemplatePage.replace(':id', id));
  };

  const dataSource = useMemo(
    () => mapLicenceConfiguratorDataSource(licences || [], weaponLicenceTemplateStatus),
    [licences, weaponLicenceTemplateStatus],
  );

  const isBusy = isLoading || isPaginationLoading;

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t('module.administration.name'), route: ERouteLinks.Administration },
          t('licenceConfiguration.header'),
        ]}
      />

      <StyledPageTitle variant="h4">{t('licenceConfiguration.header')}</StyledPageTitle>

      {initialValues && (
        <LicenceSearchForm initialSearchForm={initialValues} onSubmit={handleSetUserQuery} />
      )}

      {nextPageAlert && (
        <Box sx={{ marginBottom: '10px', marginTop: '20px', mt: 2 }}>
          <Alert text={nextPageAlert.text} variant={nextPageAlert.variant} />
        </Box>
      )}
      {samePageAlert && (
        <Box sx={{ marginBottom: '10px', marginTop: '20px', mt: 2 }}>
          <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
        </Box>
      )}

      <Grid container sx={{ justifyContent: 'end', mb: '20px', mt: '20px' }}>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <Button
            id="licence_configuration-add"
            variant={EButtonVariants.contained}
            label={t('licenceConfiguration.add.button')}
            icon={EIconTypes.plus}
            onClick={() => navigate(ERouteLinks.AddLicenceTemplatePage)}
            fullWidth
          />
        </Grid>
      </Grid>

      <Grid sx={{ display: isBusy ? 'flex' : 'none', justifyContent: 'center' }}>
        <Loader isLoading />
      </Grid>

      <Box sx={{ display: isBusy ? 'none' : 'block' }}>
        <Grid container>
          {isDesktop ? (
            <Table
              columns={licenceConfiguratorTableColumns({
                handleView,
                dateFormat: shortDateFormat,
                t,
              })}
              dataSource={dataSource}
              onSort={setSort}
              loading={false}
            />
          ) : (
            <CardList
              items={dataSource}
              render={(data, index) => (
                <LicenceConfiguratorTableCard
                  key={index}
                  data={data}
                  dateFormat={shortDateFormat}
                  handleView={handleView}
                />
              )}
            />
          )}

          {paginator && (
            <Pagination
              count={paginator.totalElements}
              onChangePage={handleChangePage}
              current={currentPage}
              perPage={perPage}
              onChangePerPage={handleChangePerPage}
              isLoading={false}
            />
          )}
        </Grid>
      </Box>
    </>
  );
};
